import {clsx} from 'clsx'
import {memo, useRef} from 'react'

import styles from './potentially-dirty.module.css'

type PotentiallyDirtyProps = {
  /**
   * Set to {@link true} to display the dirty-state indicator.
   */
  isDirty: boolean
  /**
   * Optional callback to fire if the dirty-state indicator is clicked by the user.
   */
  onClick?: React.MouseEventHandler<HTMLElement>
  /**
   * Optional field to override the `isDirty` value and hide the dirty-state indicator.
   *
   * This should be used if there are additional checks that are necessary to ensure
   * the user can save the changes, e.g. authorization or permission checks.
   *
   * Defaults to {@link false} if omitted.
   */
  hideDirtyState?: boolean
  className?: string
}

/**
 * Display an indicator to the user to indicate there are unsaved changes in a part of the application.
 */
export const PotentiallyDirty = memo<React.PropsWithChildren<PotentiallyDirtyProps>>(function PotentiallyDirty({
  children,
  isDirty,
  hideDirtyState = false,
  ...props
}) {
  const contentRef = useRef<HTMLDivElement>(null)

  const sharedProps = {
    ...props,
    title: undefined,
    ref: contentRef,
    'aria-label': 'Unsaved changes',
    role: 'status',
    className: clsx(!hideDirtyState && styles.PotentiallyDirty, children && styles.hasChildren),
  }

  if (children) {
    return (
      /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
      <div className={clsx('dirty-icon-container', styles.Box)}>
        {children}
        {isDirty && <div {...sharedProps} />}
      </div>
    )
  }

  if (!isDirty) {
    return null
  }

  return <div {...sharedProps} />
})
